import React from 'react'
import { motion } from 'framer-motion'

const VARIANTS = {
  'fade-up':    { hidden: { opacity: 0, y: 40 },   visible: { opacity: 1, y: 0 } },
  'fade-in':    { hidden: { opacity: 0 },           visible: { opacity: 1 } },
  'fade-left':  { hidden: { opacity: 0, x: -40 },  visible: { opacity: 1, x: 0 } },
  'fade-right': { hidden: { opacity: 0, x: 40 },   visible: { opacity: 1, x: 0 } },
}

/**
 * Wraps children in a motion div that animates in when scrolled into view.
 *
 * Props:
 *   variant   — 'fade-up' | 'fade-in' | 'fade-left' | 'fade-right'  (default: 'fade-up')
 *   delay     — seconds (number), e.g. 0.1                           (default: 0)
 *   duration  — seconds (number)                                     (default: 0.65)
 *   threshold — 0..1 visibility threshold                            (default: 0.12)
 *   as        — motion element tag: 'div','section','article'…       (default: 'div')
 *   className — extra classes on the wrapper
 *
 * Usage:
 *   <Reveal variant="fade-up" delay={0.1}>
 *     <MySection />
 *   </Reveal>
 *
 *   // Staggered children:
 *   {items.map((item, i) => (
 *     <Reveal key={i} delay={i * 0.08}>…</Reveal>
 *   ))}
 */
export default function Reveal({
  children,
  variant   = 'fade-up',
  delay     = 0,
  duration  = 0.9,
  threshold = 0.12,
  as        = 'div',
  className = '',
  style     = {},
  ...rest
}) {
  const Tag = motion[as] ?? motion.div
  const v   = VARIANTS[variant] ?? VARIANTS['fade-up']

  return (
    <Tag
      className={className}
      style={style}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: threshold }}
      variants={v}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </Tag>
  )
}
